/**
 * about-values.js
 * Carousel logic for the About Values section.
 * Loaded by sections/about-values.liquid
 */

if (!customElements.get('about-values-carousel')) {
  customElements.define('about-values-carousel', class AboutValuesCarousel extends HTMLElement {
    connectedCallback() {
      /** @type {HTMLElement | null} */
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      /** @type {HTMLButtonElement | null} */
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      /** @type {HTMLButtonElement | null} */
      this.nextBtn = this.querySelector('[data-carousel-next]');
      /** @type {HTMLElement | null} */
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      /** @type {HTMLButtonElement[]} */
      this.dots = [];

      this.bindEvents();

      // Defer until after paint so scrollWidth / clientWidth are fully resolved
      requestAnimationFrame(() => {
        this.buildDots();
        this.updateControls();
      });

      // Rebuild dots when the carousel is resized (breakpoint changes visible-item count)
      if (window.ResizeObserver) {
        new ResizeObserver(() => {
          requestAnimationFrame(() => {
            this.buildDots();
            this.updateControls();
          });
        }).observe(this.track);
      }
    }

    /**
     * Returns the pixel distance between two consecutive items (item width + gap).
     * Uses getBoundingClientRect instead of getComputedStyle so it works regardless
     * of how the gap is declared (CSS custom property, calc, etc.).
     */
    getItemScrollWidth() {
      if (!this.track) return 320;

      const items = /** @type {NodeListOf<HTMLElement>} */ (
        this.track.querySelectorAll('.about-values__item')
      );

      if (items.length >= 2) {
        const first = items[0];
        const second = items[1];
        if (first && second) {
          return Math.abs(second.getBoundingClientRect().left - first.getBoundingClientRect().left);
        }
      }

      const first = items[0];
      return first ? first.offsetWidth + 16 : 320;
    }

    /**
     * Builds exactly as many dots as there are reachable scroll positions.
     * This count is viewport-aware: 5 items × 3 visible = 3 dots on desktop,
     * but 4 dots on tablet (2 visible). ResizeObserver calls this on breakpoint change.
     */
    buildDots() {
      if (!this.dotsContainer || !this.track) return;

      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const itemWidth = this.getItemScrollWidth();

      if (maxScroll < 1 || itemWidth < 1) {
        this.dotsContainer.innerHTML = '';
        /** @type {HTMLButtonElement[]} */
        this.dots = [];
        return;
      }

      const count = Math.round(maxScroll / itemWidth) + 1;
      const currentDots = /** @type {HTMLButtonElement[]} */ (this.dots || []);
      if (count === currentDots.length) return; // No change — skip re-render

      this.dotsContainer.innerHTML = '';
      /** @type {HTMLButtonElement[]} */
      this.dots = [];

      for (let i = 0; i < count; i++) {
        const dot = document.createElement('button');
        dot.className = `about-values__dot${i === 0 ? ' is-active' : ''}`;
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.setAttribute('data-index', String(i));
        dot.setAttribute('aria-selected', String(i === 0));
        dot.addEventListener('click', () => {
          this.track?.scrollTo({ left: i * this.getItemScrollWidth(), behavior: 'smooth' });
        });
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      }
    }

    /**
     * Syncs the prev/next disabled states and active dot indicator
     * to the current scroll position.
     */
    updateControls() {
      if (!this.track) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      const itemWidth = this.getItemScrollWidth();

      const dots = /** @type {HTMLButtonElement[]} */ (this.dots || []);

      // Clamp so the last dot activates at max scroll even with sub-pixel rounding
      const activeIndex = dots.length > 0
        ? Math.min(Math.round(scrollLeft / itemWidth), dots.length - 1)
        : 0;

      if (this.prevBtn) this.prevBtn.disabled = scrollLeft < 1;
      // 2 px tolerance absorbs sub-pixel rounding at the scroll boundary
      if (this.nextBtn) this.nextBtn.disabled = maxScroll < 1 || scrollLeft >= maxScroll - 2;

      dots.forEach((dot, i) => {
        const active = i === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => {
        this.track?.scrollBy({ left: -this.getItemScrollWidth(), behavior: 'smooth' });
      });

      this.nextBtn?.addEventListener('click', () => {
        this.track?.scrollBy({ left: this.getItemScrollWidth(), behavior: 'smooth' });
      });

      this.track?.addEventListener('scroll', () => this.updateControls(), { passive: true });
    }
  });
}
